import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import VideoCard from '@/components/base/VideoCard';
import FilterPanel from '@/pages/category/components/FilterPanel';
import Pagination from '@/pages/category/components/Pagination';
import {
  PAGE_SIZE,
  filterVideos,
  getVideosByType,
  sortOptions,
  typeLabel,
} from '@/data/videos';
import { useSeo } from '@/hooks/useSeo';
import type { VideoType } from '@/types/video';

interface CategoryPageProps {
  type: VideoType;
}

const descriptions: Record<VideoType, string> = {
  movie: '汇集剧情长片、类型片与作者电影，可按地区、年份筛选，并按热度、评分或更新时间排序。站内条目与评分均为演示数据。',
  tv: '汇集连载剧集与单元故事，支持按地区、年份筛选查看，进入详情页可切换剧集并体验播放界面演示。',
};

export default function CategoryPage({ type }: CategoryPageProps) {
  const label = typeLabel(type);
  const allList = useMemo(() => getVideosByType(type), [type]);

  const [region, setRegion] = useState('全部');
  const [yearRange, setYearRange] = useState('all');
  const [sortBy, setSortBy] = useState('popularity');
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  useSeo({
    title: `${label}分类页 - 麻豆视频 | 高清影视资源免费在线观看`,
    description: `麻豆视频${label}分类页，提供最新热门高清${label}资源，支持多维筛选，满足多样影视观看需求。`,
    keywords: `麻豆视频,${label},高清${label},影视资源,在线观看`,
  });

  const filtered = useMemo(
    () => filterVideos(allList, region, yearRange, sortBy),
    [allList, region, yearRange, sortBy],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const updateRegion = (value: string) => {
    setRegion(value);
    setPage(1);
  };
  const updateYear = (value: string) => {
    setYearRange(value);
    setPage(1);
  };
  const updateSort = (value: string) => {
    setSortBy(value);
    setPage(1);
  };
  const resetFilters = () => {
    setRegion('全部');
    setYearRange('all');
    setSortBy('popularity');
    setPage(1);
  };
  const changePage = (next: number) => {
    setPage(next);
    const anchor = document.getElementById('category-list');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filterProps = {
    region,
    yearRange,
    sortBy,
    resultCount: filtered.length,
    onRegionChange: updateRegion,
    onYearChange: updateYear,
    onSortChange: updateSort,
    onReset: resetFilters,
  };

  return (
    <SiteLayout>
      <div className="animate-fade-in mx-auto w-full max-w-[1280px] px-4 pt-5 md:px-8 md:pt-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <span className="text-foreground-800">{label}</span>
        </nav>

        <div className="relative mt-3 overflow-hidden rounded-lg bg-primary-950 px-6 py-7 text-background-50 md:px-9 md:py-9">
          <i className="ri-film-line pointer-events-none absolute -right-4 -top-6 text-[140px] text-primary-800/50" />
          <div className="relative flex flex-wrap items-end justify-between gap-5">
            <div className="max-w-2xl">
              <span className="mb-2.5 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2 py-1 font-label text-[11px] font-bold text-primary-950">
                <i className={`${type === 'movie' ? 'ri-film-line' : 'ri-tv-2-line'} text-sm`} />
                {label}频道
              </span>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight md:text-[32px]">
                {label}分类
              </h1>
              <p className="mt-2.5 max-w-xl text-xs leading-relaxed text-primary-200 md:text-sm">
                {descriptions[type]}
              </p>
            </div>
            <div className="flex items-center gap-5 text-xs text-primary-200">
              <div>
                <p className="font-heading text-xl font-bold text-background-50">{allList.length}</p>
                <p className="mt-1">全部条目</p>
              </div>
              <span className="h-8 w-px bg-primary-800" />
              <div>
                <p className="font-heading text-xl font-bold text-accent-400">{filtered.length}</p>
                <p className="mt-1">当前筛选</p>
              </div>
            </div>
          </div>
        </div>

        <div id="category-list" className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
          <aside className="hidden self-start lg:sticky lg:top-20 lg:block">
            <FilterPanel {...filterProps} />
            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-4">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-800">
                <i className="ri-lightbulb-line text-base text-accent-600" />
                浏览提示
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                切换筛选项后列表会立即刷新并回到第一页，点击卡片可进入详情页查看剧情与播放界面。
              </p>
            </div>
          </aside>

          <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-3.5 py-2.5">
              <p className="text-xs text-foreground-500">
                共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部结果
                {safePage > 1 && <span className="ml-1">· 第 {safePage} 页</span>}
              </p>
              <div className="flex items-center gap-2">
                <label htmlFor="sort-select" className="hidden text-xs text-foreground-500 sm:block">
                  排序
                </label>
                <div className="relative">
                  <select
                    id="sort-select"
                    value={sortBy}
                    onChange={(e) => updateSort(e.target.value)}
                    className="h-8 cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-2.5 pr-7 text-xs text-foreground-800 focus:border-primary-400 focus:outline-none"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
                </div>
                <button
                  type="button"
                  onClick={() => setPanelOpen(true)}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 lg:hidden"
                >
                  <i className="ri-equalizer-line text-sm" />
                  筛选
                </button>
              </div>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                  {pageItems.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
                <Pagination currentPage={safePage} totalPages={totalPages} onChange={changePage} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-50 px-6 py-20 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-2-line text-2xl" />
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                  暂无符合条件的影片
                </h3>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
                  当前筛选组合下没有匹配内容，试着放宽地区或年份条件，或清除全部筛选重新浏览。
                </p>
                <button
                  type="button"
                  onClick={resetFilters}
                  className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  清除筛选
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="mt-8 rounded-lg bg-background-200/60 px-4 py-3 text-[11px] leading-relaxed text-foreground-500">
          说明：分类页的影视条目、地区、年份与评分均为演示数据，用于展示筛选与分页交互，不代表真实影视发行信息。
        </p>
      </div>

      <div
        className={`fixed inset-0 z-[70] lg:hidden ${panelOpen ? '' : 'pointer-events-none'}`}
        aria-hidden={!panelOpen}
      >
        <div
          onClick={() => setPanelOpen(false)}
          className={`absolute inset-0 bg-primary-950/60 transition-opacity duration-300 ${
            panelOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />
        <div
          className={`absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-lg bg-background-50 p-5 transition-transform duration-300 ${
            panelOpen ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">筛选{label}</h2>
            <button
              type="button"
              aria-label="关闭筛选"
              onClick={() => setPanelOpen(false)}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md bg-background-100 text-foreground-600"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          <FilterPanel {...filterProps} />

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={resetFilters}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 py-2.5 text-xs font-medium text-foreground-700"
            >
              重置条件
            </button>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 py-2.5 text-xs font-semibold text-primary-950"
            >
              查看 {filtered.length} 部结果
            </button>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}